import React, {FC, useCallback, useState} from 'react';

import {Handler, HandlerOf, PredicateOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {buildSettingsPath} from '../../models/url/settingsUrlHelpers';
import {
  SettingsAnswersUrl,
  SettingsResourcePagesEnum,
  SettingsRootPagesEnum,
} from '../../models/url/settingsUrlModel';
import {SettingsPanelId} from '../../store/panels/panelStateBase';
import {SettingsAnswersListContainer} from './settingsAnswersListContainer';
import {SettingsSingleAnswerContainer} from './settingsSingleAnswerContainer';

/*
 * Props.
 */

interface SettingsAnswersProps {
  panelId: SettingsPanelId;
  url: SettingsAnswersUrl;
  infoBoxId: string;
  findShouldDisplayInfoBox: PredicateOf<string>;
  navigateToLink: HandlerOf<string>;
}

/*
 * Component.
 */

export const SettingsAnswers: FC<SettingsAnswersProps> = (props) => {
  const {url: settingsAnswersUrl, navigateToLink} = props;
  const {rootPage, resourcePage} = settingsAnswersUrl;
  const [prefilledParentFolderId, setPrefilledParentFolderId] = useState<number | undefined>(undefined);

  const navigateToFolder = useCallback(
    (folderId?: number) => {
      const newUrl = buildSettingsPath({
        ...settingsAnswersUrl,
        folderId,
        inFolder: true,
        resourcePage: SettingsResourcePagesEnum.OVERVIEW,
      });

      navigateToLink(newUrl);
    },
    [navigateToLink, settingsAnswersUrl],
  );

  const openFolderEdit = useCallback(
    (folderId?: number) => {
      const newUrl = buildSettingsPath({
        ...settingsAnswersUrl,
        folderId,
        answerId: undefined,
        inFolder: true,
        resourcePage: SettingsResourcePagesEnum.EDIT,
      });

      navigateToLink(newUrl);
    },
    [navigateToLink, settingsAnswersUrl],
  );

  const openAnswerEdit = useCallback(
    (answerId?: number) => {
      const newUrl = buildSettingsPath({
        ...settingsAnswersUrl,
        answerId,
        inFolder: false,
        resourcePage: SettingsResourcePagesEnum.EDIT,
      });

      navigateToLink(newUrl);
    },
    [navigateToLink, settingsAnswersUrl],
  );

  const openNewAnswer = useCallback(
    (folderId?: number) => {
      const newUrl = buildSettingsPath({
        ...settingsAnswersUrl,
        answerId: undefined,
        folderId,
        inFolder: false,
        resourcePage: SettingsResourcePagesEnum.NEW,
      });

      if (folderId) {
        setPrefilledParentFolderId(folderId);
      }

      navigateToLink(newUrl);
    },
    [navigateToLink, settingsAnswersUrl],
  );

  const openNewFolder = useCallback(
    (folderId?: number) => {
      const newUrl = buildSettingsPath({
        ...settingsAnswersUrl,
        answerId: undefined,
        inFolder: true,
        resourcePage: SettingsResourcePagesEnum.NEW,
      });

      if (folderId) {
        setPrefilledParentFolderId(folderId);
      }

      navigateToLink(newUrl);
    },
    [navigateToLink, settingsAnswersUrl],
  );

  const openRoot = useCallback(() => {
    const newUrl = buildSettingsPath({
      ...settingsAnswersUrl,
      answerId: undefined,
      folderId: undefined,
      inFolder: false,
      resourcePage: SettingsResourcePagesEnum.OVERVIEW,
    });

    setPrefilledParentFolderId(undefined);

    navigateToLink(newUrl);
  }, [navigateToLink, settingsAnswersUrl]);

  if (rootPage !== SettingsRootPagesEnum.ANSWERS) {
    return null;
  }

  if (resourcePage === SettingsResourcePagesEnum.EDIT) {
    return (
      <SettingsSingleAnswerContainer
        navigateToFolder={navigateToFolder}
        openRoot={openRoot}
        answersUrl={settingsAnswersUrl}
        isEditing
      />
    );
  }

  if (resourcePage === SettingsResourcePagesEnum.NEW) {
    return (
      <SettingsSingleAnswerContainer
        navigateToFolder={navigateToFolder}
        openRoot={openRoot}
        answersUrl={settingsAnswersUrl}
        prefilledFolderId={prefilledParentFolderId}
      />
    );
  }

  if (resourcePage === SettingsResourcePagesEnum.OVERVIEW) {
    return (
      <SettingsAnswersListContainer
        navigateToFolder={navigateToFolder}
        openFolderEdit={openFolderEdit}
        openAnswerEdit={openAnswerEdit}
        openNewAnswer={openNewAnswer}
        openNewFolder={openNewFolder}
        openRoot={openRoot}
        settingsAnswersUrl={settingsAnswersUrl}
      />
    );
  }

  return null;
};

export interface SettingsAnswersNavigationProps {
  navigateToFolder: HandlerOf<number>;
  openFolderEdit: HandlerOf<number>;
  openAnswerEdit: HandlerOf<number>;
  openNewAnswer: (folderId?: number) => void;
  openNewFolder: (folderId?: number) => void;
  openRoot: Handler;
}
